<template>
  <div
    class="min-h-screen flex items-center justify-center py-6 px-4 sm:px-6 lg:px-8"
  >
    <div class="max-w-md w-full space-y-8">
      <!-- Header -->
      <div class="text-center">
        <div
          class="mx-auto h-16 w-16 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center mb-6 shadow-lg"
        >
          <Key
            :size="24"
            color="white"
          />
        </div>
        <h2 class="text-3xl font-bold text-gray-900 mb-2">Reset Password</h2>
        <p class="text-gray-600 text-sm">
          {{
            currentStep === 1
              ? "Enter your email to receive an OTP"
              : "Enter OTP and create new password"
          }}
        </p>
      </div>

      <!-- Success Popup -->
      <div
        v-if="showSuccessPopup"
        class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      >
        <div
          class="bg-white p-8 rounded-2xl shadow-2xl max-w-sm w-full mx-4 transform animate-pulse"
        >
          <div
            class="flex items-center justify-center w-16 h-16 mx-auto bg-green-100 rounded-full mb-6"
          >
            <svg
              class="w-8 h-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              ></path>
            </svg>
          </div>
          <p class="text-center text-gray-800 font-semibold text-lg mb-2">
            Success!
          </p>
          <p class="text-center text-gray-600 text-sm">
            An OTP has been sent to you.
          </p>
        </div>
      </div>

      <!-- Final Success Message -->
      <div
        v-if="showFinalSuccess"
        class="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6 shadow-sm"
      >
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <div
              class="h-10 w-10 bg-green-100 rounded-full flex items-center justify-center"
            >
              <svg
                class="h-6 w-6 text-green-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
          </div>
          <div class="ml-4">
            <p class="text-sm font-semibold text-green-800 mb-1">
              Password Reset Successful!
            </p>
            <p class="text-xs text-green-700">Redirecting to login page...</p>
          </div>
        </div>
      </div>

      <!-- Main Form Container -->
      <div class="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
        <!-- Step 1: Email Form -->
        <form
          v-if="currentStep === 1"
          @submit.prevent="sendOtp"
          class="space-y-6"
        >
          <div class="space-y-2">
            <label
              for="email"
              class="block text-sm font-semibold text-gray-700"
              >Email Address</label
            >
            <div class="relative">
              <div
                class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
              >
                <svg
                  class="h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                  ></path>
                </svg>
              </div>
              <input
                id="email"
                v-model="identifier"
                name="identifier"
                type="text"
                required
                class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                placeholder="Enter your email or phone number"
              />
            </div>
          </div>

          <div
            v-if="error"
            class="bg-red-50 border border-red-200 rounded-xl p-4"
          >
            <div class="flex">
              <svg
                class="h-5 w-5 text-red-400 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clip-rule="evenodd"
                />
              </svg>
              <p class="ml-3 text-sm text-red-800">{{ error }}</p>
            </div>
          </div>

          <button
            type="submit"
            :disabled="loading"
            class="w-full flex justify-center items-center py-3 px-4 border border-transparent text-sm font-semibold rounded-xl text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            <svg
              v-if="loading"
              class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {{ loading ? "Sending OTP..." : "Send OTP" }}
          </button>
        </form>

        <!-- Step 2: OTP and New Password Form -->
        <form
          v-if="currentStep === 2"
          @submit.prevent="resetPassword"
          class="space-y-6"
        >
          <div class="space-y-5">
            <!-- Email (readonly) -->
            <div class="space-y-2">
              <label
                for="email-readonly"
                class="block text-sm font-semibold text-gray-700"
                >Email Address</label
              >
              <input
                id="identifier-readonly"
                :value="identifier"
                type="text"
                readonly
                class="block w-full px-3 py-3 border border-gray-300 rounded-xl bg-gray-50 text-gray-600 text-sm sm:text-base cursor-not-allowed"
              />
            </div>

            <!-- OTP -->
            <div class="space-y-2">
              <label
                for="otp"
                class="block text-sm font-semibold text-gray-700"
                >Verification Code</label
              >
              <div class="relative">
                <div
                  class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                >
                  <svg
                    class="h-5 w-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                </div>
                <input
                  id="otp"
                  v-model="otp"
                  name="otp"
                  type="text"
                  required
                  maxlength="6"
                  pattern="[0-9]*"
                  autocomplete="one-time-code"
                  class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base text-center tracking-widest font-mono"
                  placeholder="000000"
                />
              </div>
            </div>

            <!-- New Password -->
            <div class="space-y-2">
              <label
                for="newPassword"
                class="block text-sm font-semibold text-gray-700"
                >New Password</label
              >
              <div class="relative">
                <div
                  class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                >
                  <svg
                    class="h-5 w-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    ></path>
                  </svg>
                </div>
                <input
                  id="newPassword"
                  v-model="newPassword"
                  name="newPassword"
                  type="password"
                  required
                  minlength="6"
                  class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                  placeholder="Enter new password"
                />
              </div>
            </div>

            <!-- Confirm Password -->
            <div class="space-y-2">
              <label
                for="confirmPassword"
                class="block text-sm font-semibold text-gray-700"
                >Confirm Password</label
              >
              <div class="relative">
                <div
                  class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                >
                  <svg
                    class="h-5 w-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                </div>
                <input
                  id="confirmPassword"
                  v-model="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  minlength="6"
                  class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 text-sm sm:text-base"
                  :class="{
                    'border-red-300 focus:ring-red-500':
                      confirmPassword && newPassword !== confirmPassword,
                  }"
                  placeholder="Confirm new password"
                />
              </div>
              <p
                v-if="confirmPassword && newPassword !== confirmPassword"
                class="text-red-600 text-xs mt-1 flex items-center"
              >
                <svg
                  class="h-4 w-4 mr-1"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clip-rule="evenodd"
                  />
                </svg>
                Passwords do not match
              </p>
            </div>
          </div>

          <div
            v-if="error"
            class="bg-red-50 border border-red-200 rounded-xl p-4"
          >
            <div class="flex">
              <svg
                class="h-5 w-5 text-red-400 mt-0.5"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clip-rule="evenodd"
                />
              </svg>
              <p class="ml-3 text-sm text-red-800">{{ error }}</p>
            </div>
          </div>

          <div class="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              @click="goBackToStep1"
              class="flex-1 py-3 px-4 border border-gray-300 text-sm font-semibold rounded-xl text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 order-2 sm:order-1"
            >
              Back to Email
            </button>
            <button
              type="submit"
              :disabled="
                loading ||
                !newPassword ||
                !confirmPassword ||
                newPassword !== confirmPassword
              "
              class="flex-1 flex justify-center items-center py-3 px-4 border border-transparent text-sm font-semibold rounded-xl text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 order-1 sm:order-2"
            >
              <svg
                v-if="loading"
                class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                ></circle>
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              {{ loading ? "Resetting..." : "Reset Password" }}
            </button>
          </div>
        </form>
      </div>

      <!-- Footer -->
      <div class="text-center">
        <p class="text-xs text-gray-500">
          Remember your password?
          <router-link
            to="/login"
            class="font-medium text-indigo-600 hover:text-indigo-500 transition-colors duration-200"
          >
            Sign in here
          </router-link>
        </p>
      </div>
    </div>
  </div>
</template>
<script setup>
  import { ref, computed } from "vue";
  import { useRouter } from "vue-router";
  import { Key } from "lucide-vue-next";

  const router = useRouter();
  const baseURL = process.env.VUE_APP_BASE_URL || "http://localhost:5000";

  // Reactive state
  const currentStep = ref(1);
  const identifier = ref(""); // unified field for email or phone
  const otp = ref("");
  const newPassword = ref("");
  const confirmPassword = ref("");
  const loading = ref(false);
  const error = ref("");
  const showSuccessPopup = ref(false);
  const showFinalSuccess = ref(false);

  // ✅ Computed password validity
  const isPasswordValid = computed(() => {
    return (
      newPassword.value &&
      confirmPassword.value &&
      newPassword.value === confirmPassword.value
    );
  });

  // ✅ Helper validators
  const isEmail = (value) => /\S+@\S+\.\S+/.test(value);
  const isPhone = (value) => /^[0-9]{10}$/.test(value);

  // ✅ Send OTP
  const sendOtp = async () => {
    loading.value = true;
    error.value = "";

    try {
      let payload = {};

      if (isEmail(identifier.value)) {
        payload.email = identifier.value;
      } else if (isPhone(identifier.value)) {
        payload.phone = identifier.value;
      } else {
        error.value = "Please enter a valid email or phone number.";
        loading.value = false;
        return;
      }

      const response = await fetch(`${baseURL}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && data.message?.includes("OTP sent")) {
        showSuccessPopup.value = true;
        setTimeout(() => {
          showSuccessPopup.value = false;
          currentStep.value = 2;
        }, 2500);
      } else {
        error.value = data.error || "Failed to send OTP.";
      }
    } catch (err) {
      error.value = "Network error. Please try again.";
    } finally {
      loading.value = false;
    }
  };

  // ✅ Reset password
  const resetPassword = async () => {
    if (!isPasswordValid.value) {
      error.value = "Passwords do not match.";
      return;
    }

    loading.value = true;
    error.value = "";

    try {
      let payload = {
        otp: otp.value,
        newPassword: newPassword.value,
      };

      if (isEmail(identifier.value)) {
        payload.email = identifier.value;
      } else if (isPhone(identifier.value)) {
        payload.phone = identifier.value;
      } else {
        error.value = "Invalid email or phone.";
        loading.value = false;
        return;
      }

      const response = await fetch(`${baseURL}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && data.message?.includes("Password reset successful")) {
        showFinalSuccess.value = true;
        setTimeout(() => {
          router.push("/login");
        }, 2000);
      } else {
        error.value = data.error || "Failed to reset password.";
      }
    } catch (err) {
      error.value = "Network error. Please try again.";
    } finally {
      loading.value = false;
    }
  };

  // ✅ Go back
  const goBackToStep1 = () => {
    currentStep.value = 1;
    otp.value = "";
    newPassword.value = "";
    confirmPassword.value = "";
    error.value = "";
  };
</script>
